import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, ExternalLink, TrendingUp, Users, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductCard = ({ product }) => {
  const { toast } = useToast();

  const handleAffiliateClick = async () => {
    try {
      // Track click in backend
      await axios.post(`${BACKEND_URL}/api/clicks`, {
        product_id: product.id.toString(),
        product_name: product.name,
        affiliate_network: product.affiliateNetwork,
        commission_amount: product.commissionAmount
      });

      toast({
        title: "🎯 Opening Affiliate Link!",
        description: `${product.name} - Your commission: ${product.earnings}`,
      });

      // Open actual affiliate link in new tab
      window.open(product.affiliateLink, '_blank');

    } catch (error) {
      console.error('Error tracking click:', error);
      // Still open link even if tracking fails
      window.open(product.affiliateLink, '_blank');
      toast({
        title: "Opening Product...",
        description: `${product.name} - Your commission: ${product.earnings}`,
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group">
      {/* Image */}
      <div className="relative overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {product.badge && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-emerald-600 text-white px-3 py-1 font-semibold">
              {product.badge}
            </Badge>
          </div>
        )}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
          {product.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
          {product.name}
        </h3>
        
        {/* Rating */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-600">
            {product.rating} ({product.reviews.toLocaleString()} reviews)
          </span>
        </div>

        <p className="text-gray-600 mb-4 line-clamp-2">
          {product.description}
        </p>

        {/* Features */}
        <div className="space-y-2 mb-4">
          {product.features.slice(0, 3).map((feature, index) => (
            <div key={index} className="flex items-start space-x-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-emerald-50 rounded-lg">
          <div>
            <div className="flex items-center space-x-1 text-emerald-700 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-semibold">Conversion</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{product.conversionRate}</div>
          </div>
          <div>
            <div className="flex items-center space-x-1 text-emerald-700 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-semibold">Gravity</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{product.gravity}</div>
          </div>
        </div>

        {/* Price & Commission */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <div>
            <div className="text-sm text-gray-600">Product Price</div>
            <div className="text-2xl font-bold text-gray-900">${product.price}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">You Earn</div>
            <div className="text-2xl font-bold text-emerald-600">${product.commissionAmount}</div>
            <div className="text-xs text-gray-500">{product.commission}% commission</div>
          </div>
        </div>

        {/* Network */}
        <div className="mb-4">
          <span className="text-xs text-gray-600">Affiliate Network: </span>
          <span className="text-xs font-semibold text-gray-900">{product.affiliateNetwork}</span>
        </div>

        {/* CTA Button */}
        <Button 
          onClick={handleAffiliateClick}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 group"
        >
          Get Affiliate Link
          <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
};
export default ProductCard;
